import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import { font } from '../../utils/fontHelper';
import { stashMovieDetailNavPreviewFromMovie } from '../../utils/movieDetailNavPreview';
import PostActionBar from './PostActionBar';
import CompactMovieCard from './CompactMovieCard';
import { useMovieQuickView } from '../../contexts/MovieQuickViewContext';
import { relativeTime } from '../../utils/crewFeedUtils';
import { formatWatchedDate } from '../../utils/dateUtils';

export interface PublicReviewCardData {
  id: string;
  movieId: string;
  movieTitle: string;
  moviePoster?: string;
  movieBackdrop?: string;
  movieOverview?: string;
  releaseDateLabel?: string;
  movieGenres?: string[];
  rating?: number;
  review: string;
  dateLabel: string;
  watchedDateLabel?: string;
  watchedDate?: string;
  watchedDatePrecision?: 'day' | 'month' | 'year';
  updatedDateLabel: string;
  updatedDate?: string;
  sortValue: number;
  likes?: number;
  likedBy?: string[];
  comments?: number;
  isPrivate?: boolean;
  visibility?: string;
}

interface PublicReviewCardProps {
  review: PublicReviewCardData;
  onMovieSelect?: (movieId: string) => void;
  onToggleLike?: (reviewId: string) => void;
  onReviewClick?: (review: PublicReviewCardData) => void;
  onLikedByClick?: (userIds: string[]) => void;
  /** Kept for callers; heart state is derived from review.likedBy + currentUserId. */
  isLiked?: boolean;
  currentUserId?: string;
  layout?: 'vertical' | 'horizontal';
  hideActionBar?: boolean;
}

const PublicReviewCard: React.FC<PublicReviewCardProps> = ({
  review,
  onToggleLike,
  onReviewClick,
  onLikedByClick,
  isLiked: _isLiked,
  currentUserId,
  layout = 'vertical',
  hideActionBar = false,
}) => {
  const theme = useTheme();
  const { openMovieQuickView } = useMovieQuickView();
  const showLiked = Boolean(
    currentUserId && review.likedBy?.includes(currentUserId)
  );
  const hasPoster = Boolean(review.moviePoster);

  const handleCardClick = () => {
    if (onReviewClick) {
      onReviewClick(review);
    }
  };

  const handleMovieClick = () => {
    // Stash movie preview data for instant display
    stashMovieDetailNavPreviewFromMovie(
      review.movieId,
      {
        Title: review.movieTitle,
        title: review.movieTitle,
      },
      () => review.moviePoster || null
    );

    openMovieQuickView(review.movieId);
  };

  return (
    <Card
      component="article"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleCardClick();
        }
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 3,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        width: layout === 'horizontal' ? { xs: '70vw', sm: 320 } : '100%',
        minWidth: layout === 'horizontal' ? 240 : 'auto',
        maxWidth: layout === 'horizontal' ? 400 : '100%',
        flexShrink: 0,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 24px rgba(0,0,0,0.35)'
            : '0 12px 24px rgba(0,0,0,0.12)',
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        }
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 1,
          width: '100%',
          p: { xs: 1.5, sm: 2 },
          '&:last-child': { pb: { xs: 1, sm: 1 } }
        }}
      >
        <Box
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ '& > *': { bgcolor: 'transparent !important', p: '0 !important' } }}
        >
          <CompactMovieCard
            noBorder
            dense
            poster={review.moviePoster}
            title={review.movieTitle}
            year={review.releaseDateLabel}
            genre={review.movieGenres?.[0]}
            rating={review.rating}
            onClick={handleMovieClick}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary }}>
            {relativeTime(review.sortValue)}
          </Typography>
          {review.watchedDate && (
            <>
              <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>•</Typography>
              <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary }}>
                {`watched ${formatWatchedDate(review.watchedDate, review.watchedDatePrecision, '')}`}
              </Typography>
            </>
          )}
        </Box>

        <Typography
          sx={{
            fontFamily: font(),
            fontSize: 14,
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: layout === 'horizontal' ? (hasPoster ? 3 : 4) : 4,
            mt: 0.75,
            overflow: 'hidden',
            textAlign: 'left',
            minHeight: layout === 'horizontal' ? '6em' : 'auto'
          }}
        >
          {review.review}
        </Typography>

        {/* Like + Comment counts via unified action bar */}
        {!hideActionBar && <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', ml: -0.5 }}>
          <PostActionBar
            likeCount={review.likes || 0}
            isLiked={showLiked}
            commentCount={review.comments || 0}
            onLikeToggle={() => {
              if (onToggleLike) onToggleLike(review.id);
            }}
            onCommentClick={() => onReviewClick?.(review)}
            onLikedByClick={onLikedByClick ? () => onLikedByClick(review.likedBy || []) : undefined}
            disabled={!currentUserId}
            dense
          />
        </Box>}
      </CardContent>
    </Card>
  );
};

export default PublicReviewCard;
